import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { finalize, map, Observable, shareReplay, tap } from 'rxjs';
import { UserModel } from 'shared';
import { ACCESS_TOKEN_KEY } from 'symbols';
import { CheckConfirmCodeModel, LoginData, LoginResponse, RegistrationData, ResetPasswordModel } from '../symbols';

/** Сервер відхилив сесію (на відміну від мережевої помилки чи 5xx, після яких сесію треба зберегти). */
export function isAuthRejected(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  return status === 401 || status === 403;
}

@Injectable()
export class GpAuthService {
  private refreshInFlight$: Observable<string> | null = null;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: string) {
  }

  public login(data: LoginData): Observable<string> {
    return this.http.post<LoginResponse>('/api/auth/login/', data)
      .pipe(map(res => res.access_token));
  }

  public saveToken(token: string): void {
    if (!isPlatformServer(this.platformId) && token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }
  }

  public getToken(): string {
    if (!isPlatformServer(this.platformId)) {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    return null;
  }

  public logout(): Observable<void> {
    return this.http.post<void>('/api/auth/logout/', {});
  }

  public clearToken(): void {
    if (!isPlatformServer(this.platformId)) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }

  /**
   * Refresh-токен одноразовий: два паралельні запити з однією cookie — і другий отримує 401.
   * Тому всі одночасні виклики ділять один запит. Новий access-токен зберігається тут же.
   */
  public refreshToken(): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post<LoginResponse>('/api/auth/refresh-token/', {}).pipe(
        map(res => res.access_token),
        tap(token => this.saveToken(token)),
        finalize(() => this.refreshInFlight$ = null),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.refreshInFlight$;
  }

  public registration(data: RegistrationData, token: string): Observable<UserModel> {
    return this.http.post<UserModel>('/api/auth/registration/', data, { headers: { Authorization: `Bearer ${token}` } });
  }

  public getConfirmCodeToTelegramBot(tel: string): Observable<void> {
    return this.http.post<void>('/api/auth/get-confirm-code/', { tel });
  }

  public checkUserExist(tel: string): Observable<boolean> {
    return this.http.post<{ isExist: boolean }>('/api/auth/is-user-exist/', { tel }).pipe(map(res => res.isExist));
  }

  public checkTelegramBotExist(tel: string): Observable<boolean> {
    return this.http.post<{
      isExist: boolean
    }>('/api/auth/is-telegram-bot-exist', { tel }).pipe(map(res => res.isExist));
  }

  public getTelegramBotLink(): Observable<string> {
    return this.http.get<{ link: string }>('/api/telegram/bot-link').pipe(map(res => res.link));
  }

  public checkRegistrationConfirmCode(data: CheckConfirmCodeModel): Observable<string> {
    return this.http.post<{
      registrationToken: string
    }>('/api/auth/check-confirm-code', data).pipe(map(res => res.registrationToken));
  }


  public checkResetPasswordConfirmCode(data: CheckConfirmCodeModel): Observable<string> {
    return this.http.post<{
      registrationToken: string
    }>('/api/auth/check-reset-password-code', data).pipe(map(res => res.registrationToken));
  }

  public resetPassword(data: ResetPasswordModel, token: string): Observable<void> {
    return this.http.post<void>('/api/auth/reset-password/', data, { headers: { Authorization: `Bearer ${token}` } });
  }

  public inactivateAccount(): Observable<void> {
    return this.http.delete<void>('/api/auth/');
  }
}
